import { type Raw, computed, toValue } from 'vue'

import { useToastsStore } from '@global-store/stores/toast'
import {
  triggerAddWishlistItemEvent,
  triggerRemoveWishlistItemEvent,
} from '@shared/event/events/wishlist'
import { t } from '@shared/fondof-fn/translations/translations'
import { useCustomerStore } from '@shared/global-store/stores/customer/customer'
import { useWishlistStore } from '@shared/global-store/stores/wishlist/wishlist'
import { getProducts } from '@shared/shop-api/product/api'

export type UseWithlistItemOptions = {
  icons?: {
    trashCan?: Raw<object>
    heartRed?: Raw<object>
    wavehand?: Raw<object>
  }
}

export const useWishlistItem = (
  id: string,
  options?: UseWithlistItemOptions
) => {
  const productId = toValue(id)

  const wishlistStore = useWishlistStore()
  const toastsStore = useToastsStore()
  const customerStore = useCustomerStore()

  const isItemActive = computed(() =>
    wishlistStore.wishlistItemIds?.includes(productId)
  )

  const loginUrl = customerStore?.authorizationUrl ?? ''

  const removeFromWishlistItem = async () => {
    await wishlistStore.removeItem(productId)
    toastsStore.addInfoToast(t('wishlist.deleteProduct'), {
      position: {
        mobile: 'bottom-center',
        desktop: 'bottom-center',
      },
      icon: options?.icons?.trashCan,
    })

    void triggerRemoveWishlistItem([productId])
  }

  const addToWishlistItem = async () => {
    if (!customerStore.isAuthenticated) {
      toastsStore.addInfoToast(
        t('wishlist.loginRequired', { link: loginUrl }),
        {
          position: {
            mobile: 'bottom-center',
            desktop: 'bottom-center',
          },
          icon: options?.icons?.wavehand,
        }
      )
      return
    }
    await wishlistStore.setWishlistItem(productId)
    toastsStore.addInfoToast(t('wishlist.addProduct'), {
      position: {
        mobile: 'bottom-center',
        desktop: 'bottom-center',
      },
      icon: options?.icons?.heartRed,
    })

    void triggerAddWishlistItem([productId])
  }

  const toggleWishlistItem = () => {
    if (!wishlistStore.wishlistItemIds) {
      return
    }

    if (wishlistStore.wishlistItemIds.includes(productId)) {
      removeFromWishlistItem()
      return
    }

    addToWishlistItem()
  }

  return {
    removeFromWishlistItem,
    addToWishlistItem,
    toggleWishlistItem,
    isItemActive,
  }
}

const triggerRemoveWishlistItem = async (productIds: string[]) => {
  const products = await getProducts(productIds)

  triggerRemoveWishlistItemEvent({
    wishlistItems: products,
  })
}

const triggerAddWishlistItem = async (productIds: string[]) => {
  const products = await getProducts(productIds)

  triggerAddWishlistItemEvent({
    wishlistItems: products,
  })
}
